import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import WebApp from '@twa-dev/sdk';
import { ArrowLeft } from 'lucide-react';
import { api } from '../api/api';
import { useStore } from '../store/useStore';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

export function CheckoutPage() {
  const navigate = useNavigate();
  const { cart, clearCart, calculateTotal } = useStore();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    deliveryAddress: '',
    phone: '',
    comment: '',
  });

  const total = calculateTotal();
  const userId = WebApp.initDataUnsafe.user?.id?.toString() || '0';

  useEffect(() => {
    WebApp.BackButton.show();
    WebApp.BackButton.onClick(() => navigate('/cart'));

    return () => {
      WebApp.BackButton.hide();
    };
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.deliveryAddress || !formData.phone) {
      WebApp.showAlert('Пожалуйста, заполните адрес доставки и телефон');
      return;
    }

    try {
      setLoading(true);

      const orderData = {
        userId,
        items: cart.map(item => ({
          productId: item.productId,
          quantity: item.quantity,
        })),
        deliveryAddress: formData.deliveryAddress,
        phone: formData.phone,
        comment: formData.comment,
      };

      const order = await api.createOrder(orderData);

      clearCart();

      WebApp.showPopup({
        title: 'Заказ оформлен!',
        message: `Ваш заказ #${order.id} принят в обработку. Мы свяжемся с вами в ближайшее время.`,
        buttons: [{ type: 'ok' }],
      }, () => {
        navigate('/orders');
      });
    } catch (error) {
      console.error(error);
      WebApp.showAlert('Ошибка при оформлении заказа. Попробуйте еще раз.');
    } finally {
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="min-h-screen pb-32">
      <header className="sticky top-0 z-10 bg-background border-b px-4 py-5 flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/cart')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-xl font-bold">Оформление заказа</h1>
      </header>

      <form onSubmit={handleSubmit} className="p-4 space-y-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Контактные данные</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="phone" className="text-sm font-medium">
                Телефон <span className="text-destructive">*</span>
              </label>
              <input
                type="tel"
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+7 (___) ___-__-__"
                required
                className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="address" className="text-sm font-medium">
                Адрес доставки <span className="text-destructive">*</span>
              </label>
              <textarea
                id="address"
                value={formData.deliveryAddress}
                onChange={(e) => setFormData({ ...formData, deliveryAddress: e.target.value })}
                placeholder="Улица, дом, квартира"
                rows={3}
                required
                className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="comment" className="text-sm font-medium">
                Комментарий к заказу
              </label>
              <textarea
                id="comment"
                value={formData.comment}
                onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                placeholder="Дополнительная информация"
                rows={2}
                className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary resize-none"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Ваш заказ</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {cart.map(item => (
              <div key={item.id} className="flex justify-between items-center text-sm">
                <span className="text-muted-foreground">
                  {item.product.name} × {item.quantity}
                </span>
                <span className="font-medium">
                  {(parseFloat(item.product.price) * item.quantity).toFixed(0)} ₽
                </span>
              </div>
            ))}

            <div className="border-t pt-3 flex justify-between items-center">
              <span className="font-semibold">Итого:</span>
              <span className="text-xl font-bold text-primary">{total.toFixed(0)} ₽</span>
            </div>
          </CardContent>
        </Card>
      </form>

      <div className="fixed bottom-[calc(4rem+env(safe-area-inset-bottom))] left-0 right-0 bg-background border-t p-4">
        <Button
          size="lg"
          className="w-full"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Оформление...' : `Оформить заказ на ${total.toFixed(0)} ₽`}
        </Button>
      </div>
    </div>
  );
}
